import React, { FC, ReactNode, RefObject, useCallback, useMemo } from 'react';
import StateManager from 'react-select';
import type { ProjectId } from 'api/models';
import { FieldOptionBase } from 'api/tasks';
import { VirtualizedMenuList } from 'components/atoms/select/custom-components';
import { MultiUserField } from 'components/organisms/tasks/fields';
import { ClassNameOnlyProps } from 'components/ozone-component';
import { useUserFieldOptions } from 'hooks/use-user-field-options';
import { byLabel } from 'utils/comparators/by-label';
import { Popover, Select } from '../components/components';

type Props = ClassNameOnlyProps & {
  field: MultiUserField;
  title: ReactNode;
  onChange: (latest: number[]) => void;
  projectId: ProjectId;
  isPrivate: boolean;
  selectRef: RefObject<StateManager>;
};

export const ControlledMultiUserFieldEditorPopover: FC<Props> = ({
  field,
  projectId,
  isPrivate,
  title,
  onChange,
  selectRef,
  className,
}) => {
  const options = useUserFieldOptions({
    projectId,
    isPrivate,
  });
  const onValueChange = useCallback(
    (latest: FieldOptionBase[]) => {
      return onChange?.((latest ?? []).sort(byLabel).map((x) => x.value));
    },
    [onChange],
  );

  const usersInOptions = useMemo(
    () => options.flatMap((option) => option.options),
    [options],
  );

  const selectedUsers = useMemo(
    () =>
      (field.value ?? [])
        .map((id) => {
          return usersInOptions?.find((u) => u?.value === id);
        })
        .filter(Boolean),
    [field.value, usersInOptions],
  );

  return (
    <Popover className={className}>
      <Popover.Title>{title}</Popover.Title>
      <Select
        ref={selectRef}
        type="user"
        isMulti
        components={{ MenuList: VirtualizedMenuList }}
        options={options}
        value={selectedUsers}
        onChange={onValueChange}
        grouped
      />
    </Popover>
  );
};
